import { caseStudies, type CaseStudy } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

const levelStyle: Record<string, string> = {
  高风险: "bg-primary-500 text-background-50",
  中风险: "bg-accent-500 text-foreground-950",
  关注: "bg-secondary-500 text-background-50",
};

function LevelTag({ level }: { level: CaseStudy["level"] }) {
  return (
    <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold ${levelStyle[level]}`}>{level}</span>
  );
}

function LargeCard({ item }: { item: CaseStudy }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-7 lg:row-span-2">
      <div className="relative h-52 w-full overflow-hidden md:h-72">
        <img
          src={item.image}
          alt={`${item.title} - 17黑料网合作避雷案例`}
          title={`${item.brand} 代言风险案例`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span className="rounded-sm bg-foreground-950/80 px-2 py-0.5 text-[10px] font-semibold text-background-50">
            {item.category}
          </span>
          <LevelTag level={item.level} />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <p className="text-xs font-semibold text-primary-600">{item.brand}</p>
        <h4 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
          {item.title}
        </h4>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-background-200 pt-4">
          {item.metrics.map((m) => (
            <div key={m.label}>
              <p className="font-heading text-base font-black text-foreground-950">{m.value}</p>
              <p className="text-[11px] text-foreground-500">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function MediumCard({ item }: { item: CaseStudy }) {
  return (
    <article className="group flex overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-5">
      <div className="relative hidden w-32 shrink-0 sm:block">
        <img
          src={item.image}
          alt={`${item.title} - 17黑料网合作避雷案例`}
          title={`${item.brand} 代言风险案例`}
          className="h-full w-full object-cover object-top"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          <span className="rounded-sm bg-background-200 px-2 py-0.5 text-[10px] font-semibold text-foreground-700">
            {item.category}
          </span>
          <LevelTag level={item.level} />
        </div>
        <p className="mt-2 text-[11px] font-semibold text-primary-600">{item.brand}</p>
        <h4 className="mt-1 text-sm font-bold leading-snug text-foreground-950 md:text-base">{item.title}</h4>
        <p className="mt-2 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          {item.metrics.map((m) => (
            <span key={m.label} className="text-[11px] text-foreground-500">
              {m.label}
              <strong className="ml-1 text-foreground-900">{m.value}</strong>
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

function SmallCard({ item }: { item: CaseStudy }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-4">
      <div className="relative h-36 w-full overflow-hidden">
        <img
          src={item.image}
          alt={`${item.title} - 17黑料网合作避雷案例`}
          title={`${item.brand} 代言风险案例`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3">
          <LevelTag level={item.level} />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <span className="text-[10px] font-semibold text-foreground-500">
          {item.category} · {item.brand}
        </span>
        <h4 className="mt-1.5 text-sm font-bold leading-snug text-foreground-950">{item.title}</h4>
        <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
        {item.metrics[0] && (
          <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
            <span className="text-[11px] text-foreground-500">{item.metrics[0].label}</span>
            <strong className="font-heading text-sm font-black text-foreground-950">
              {item.metrics[0].value}
            </strong>
          </div>
        )}
      </div>
    </article>
  );
}

export default function CaseGrid() {
  const [large, medium1, medium2, ...smalls] = caseStudies;

  return (
    <section id="case-grid" className="w-full border-b border-background-200 bg-background-50 py-10 md:py-14">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <SectionHeading
          no="03"
          title="合作避雷案例"
          desc="把「官宣当天反转」「续约前复核」「道歉后的复盘」这些真实场景拆开看，避雷从来不是一句判断，而是一串可复用的动作。"
          actionLabel="更多案例"
          actionHref="#case-grid"
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:auto-rows-min">
          <LargeCard item={large} />
          <MediumCard item={medium1} />
          <MediumCard item={medium2} />
          {smalls.map((item) => (
            <SmallCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}